import { Injectable, OnDestroy, Inject } from '@angular/core';
import { ComponentTranslations } from '../interface';
import { TranslocoService, TRANSLOCO_CONFIG, TranslocoConfig } from '@ngneat/transloco';
import { distinctUntilChanged, filter, mergeMap } from 'rxjs/operators';
import { takeUntilDestroyed, filterEmpty, DecorateUntilDestroy } from '@skyeng/libs/shared/rxjs';
import { of, merge } from 'rxjs';
import { MissingTranslationHelperService } from './missing-translation-helper';

@DecorateUntilDestroy()
@Injectable({ providedIn: 'root' })
export class TranslationLoaderService implements OnDestroy {
  private registeredScopes: string[] = [];

  constructor(
    @Inject(TRANSLOCO_CONFIG) private translocoConfig: TranslocoConfig,
    private translocoService: TranslocoService,
    private missingTranslationHelperService: MissingTranslationHelperService,
  ) {}

  ngOnDestroy(): void {}

  registerTranslations(scope: string, translations: ComponentTranslations): void {
    // prevent same scope double registration
    if (this.registeredScopes.includes(scope)) {
      return;
    }

    this.registeredScopes.push(scope);

    const defaultLang$ = of(this.translocoConfig.fallbackLang as string | undefined);
    const loadedLang: string[] = [];

    // always load fallback lang translation
    merge(defaultLang$, this.translocoService.langChanges$)
      .pipe(
        filterEmpty(),
        distinctUntilChanged(),
        filter(lang => !!translations[lang] && !loadedLang.includes(lang)),
        mergeMap(lang => this.loadTranslation(translations, lang, scope)),
        filter(({ translation }) => !!translation),
        takeUntilDestroyed(this),
      )
      .subscribe(({ translation, lang }) => {
        this.translocoService.setTranslation({ [scope]: translation! }, lang);
        // eslint-disable-next-line functional/immutable-data
        loadedLang.push(lang);

        this.missingTranslationHelperService.setLoadedStatus(scope, translation);
      });
  }

  private loadTranslation(
    translations: ComponentTranslations,
    lang: string,
    scope: string,
  ): Promise<{ translation: Object | null; lang: string }> {
    const translation = translations[lang];
    const translationLoader = typeof translation === 'function' ? translation : () => translation;

    // `Promise.resolve(promise).then()` === `promise.then()`
    return Promise.resolve(translationLoader())
      .then(translation => ({
        translation: translation && translation['default'] ? translation['default'] : translation,
        lang,
      }))
      .catch(() => {
        console.error(`TranslationLoaderService cant load "${lang}" translation for "${scope}" scope`);

        return { translation: null, lang };
      });
  }
}
